import { Entity } from '@api/api/entity-schema';
import { formatTicker } from '@api/mappers/formatTicker';
import { Button } from '@design/components/button/Button';
import { EntityDetails } from '@design/widgets/entity-details/EntityDetails';
import { AssetLogoContainer } from '@terminal/components/asset-logo-container/AssetLogoContainer';
import { WatchlistTrigger } from '@terminal/components/watchlist/watchlist-trigger/WatchlistTrigger';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import { Badge } from '@toggle/design-system/src/components/badge/Badge';
import clsx from 'clsx';
import { ReactNode } from 'react';
import styles from './EntityHeader.module.scss';

export interface EntityHeaderProps {
  entity: Entity;
  priceDisplayNode: ReactNode;
}

const DEFAULT_ICON_SIZE = 48;

export const EntityHeader = ({
  entity,
  priceDisplayNode,
}: EntityHeaderProps) => {
  const { lists, listIds } = useWatchlist(state => ({
    listIds: state.listIdsByTag.get(entity.tag),
    lists: state.lists,
  }));
  const ticker = formatTicker(entity);
  const entityName = entity.name_short || entity.name_full;
  const watchlistNames = listIds
    ?.map(listId => lists.find(list => list.id === listId)?.name)
    .slice(0, 3);

  return (
    <EntityDetails
      className={styles.styledEntityDetails}
      entityNameComponent={
        <>
          <span data-testid="text">{entityName}</span>
          <WatchlistTrigger
            tag={entity.tag}
            dropdownProps={{
              strategy: 'fixed',
              placement: 'top-start',
              inPortal: false,
            }}
            renderTrigger={isInWatchlist => {
              return (
                <Button
                  className={clsx(styles.watchlistButton, {
                    [styles.active]: isInWatchlist,
                  })}
                  data-testid="watchlist-button"
                  size="xsmall"
                  variant="tertiary"
                  iconName={isInWatchlist ? 'Checkmark' : 'Add'}
                />
              );
            }}
          />
        </>
      }
      priceDisplayNode={priceDisplayNode}
      formattedTicker={ticker}
      watchlistPillsNode={
        watchlistNames && (
          <>
            {watchlistNames.map(name => (
              <Badge
                key={name}
                variant="textual"
                leftIcon="Watchlist"
                label={name}
                color="soft"
              />
            ))}
          </>
        )
      }
      leftImgNode={
        <AssetLogoContainer size={DEFAULT_ICON_SIZE} entity={entity} />
      }
    />
  );
};
